import get from 'lodash/get';
import axiosConfig from './axiosConfig';

const DOMAIN = 'spacer_log';

const unwrap = (res) => {
  const top = get(res, 'data', null);
  const nested = get(res, ['data', 'data'], null);
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) return nested;
  if (top && typeof top === 'object' && !Array.isArray(top)) return top;
  return null;
};

const coerceUsageObject = (raw) => {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
  return null;
};

const estimateCostUsd = (inputTokens, outputTokens) => {
  const inTok = Number.isFinite(inputTokens) ? inputTokens : 0;
  const outTok = Number.isFinite(outputTokens) ? outputTokens : 0;
  // Match pih-server estimateInsightCost defaults (Sonnet-ish list prices).
  const usd = (inTok / 1_000_000) * 3 + (outTok / 1_000_000) * 15;
  return Math.round(usd * 100) / 100;
};

const extractUsageFields = (src) => {
  if (!src || typeof src !== 'object') return null;
  const inputTokens = Number(
    src.inputTokens
      ?? src.input_tokens
      ?? src.promptTokens
      ?? src.prompt_tokens
      ?? src.input
      ?? NaN
  );
  const outputTokens = Number(
    src.outputTokens
      ?? src.output_tokens
      ?? src.completionTokens
      ?? src.completion_tokens
      ?? src.output
      ?? NaN
  );
  let estimatedCostUsd = Number(
    src.estimatedCostUsd
      ?? src.estimated_cost_usd
      ?? src.estCostUsd
      ?? src.costUsd
      ?? src.cost_usd
      ?? src.totalCost
      ?? src.total_cost
      ?? src.cost
      ?? NaN
  );
  const hasTokens = Number.isFinite(inputTokens) || Number.isFinite(outputTokens);
  if (!Number.isFinite(estimatedCostUsd) && hasTokens) {
    estimatedCostUsd = estimateCostUsd(inputTokens, outputTokens);
  }
  const hasAny =
    Number.isFinite(inputTokens) || Number.isFinite(outputTokens) || Number.isFinite(estimatedCostUsd);
  if (!hasAny) return null;
  return {
    inputTokens: Number.isFinite(inputTokens) ? inputTokens : null,
    outputTokens: Number.isFinite(outputTokens) ? outputTokens : null,
    estimatedCostUsd: Number.isFinite(estimatedCostUsd) ? estimatedCostUsd : null,
  };
};

/**
 * Normalize usage from generate or chat-history assistant payloads.
 * Accepts nested usage / snake_case / stringified JSON / flat message fields.
 */
export const normalizeUsage = (raw) => {
  const parsed = coerceUsageObject(raw);
  if (!parsed) return null;

  const nestedKeys = ['usage', 'tokenUsage', 'token_usage', 'tokens', 'cost'];
  for (let i = 0; i < nestedKeys.length; i += 1) {
    const nested = coerceUsageObject(parsed[nestedKeys[i]]);
    const hit = extractUsageFields(nested);
    if (hit) return hit;
  }

  const fromMeta = extractUsageFields(coerceUsageObject(parsed.meta?.usage || parsed.metadata?.usage));
  if (fromMeta) return fromMeta;

  return extractUsageFields(parsed);
};

/** Pull usage from a chat-history assistant message (any known shape). */
export const pickUsageFromMessage = (m) => {
  if (!m || typeof m !== 'object') return null;
  return (
    normalizeUsage(m.usage)
    || normalizeUsage(m.tokenUsage)
    || normalizeUsage(m.token_usage)
    || normalizeUsage(m.meta)
    || normalizeUsage(m.metadata)
    || normalizeUsage(m.stats)
    || normalizeUsage({
      inputTokens: m.inputTokens ?? m.input_tokens,
      outputTokens: m.outputTokens ?? m.output_tokens,
      estimatedCostUsd: m.estimatedCostUsd ?? m.estimated_cost_usd ?? m.costUsd,
    })
    || null
  );
};

const USAGE_CACHE_KEY = 'askEvansUsageByFeedbackId';

/** Remember per-answer usage so history reopen still shows it in this browser session. */
export const cacheUsageForFeedbackTarget = (feedbackTargetId, usage) => {
  const u = normalizeUsage(usage);
  if (feedbackTargetId == null || feedbackTargetId === '' || !u) return;
  try {
    const map = JSON.parse(sessionStorage.getItem(USAGE_CACHE_KEY) || '{}');
    map[String(feedbackTargetId)] = u;
    sessionStorage.setItem(USAGE_CACHE_KEY, JSON.stringify(map));
  } catch {
    /* ignore quota / private mode */
  }
};

export const readCachedUsage = (feedbackTargetId) => {
  if (feedbackTargetId == null || feedbackTargetId === '') return null;
  try {
    const map = JSON.parse(sessionStorage.getItem(USAGE_CACHE_KEY) || '{}');
    return normalizeUsage(map[String(feedbackTargetId)]);
  } catch {
    return null;
  }
};

export const normalizeQuota = (raw) => {
  if (!raw || typeof raw !== 'object') return null;

  const usedRaw = Number(raw.used ?? raw.tokensUsed ?? raw.tokens_used ?? NaN);
  const limitRaw = Number(raw.limit ?? raw.tokensLimit ?? raw.tokens_limit ?? NaN);
  const percentRaw = Number(raw.percent ?? raw.percentage ?? NaN);

  const hasUsed = Number.isFinite(usedRaw);
  const hasLimit = Number.isFinite(limitRaw) && limitRaw > 0;
  const hasPercent = Number.isFinite(percentRaw);

  // Need at least a usable percent, or used+limit (used may be 0).
  if (!hasPercent && !(hasUsed && hasLimit)) return null;

  const used = hasUsed ? Math.max(0, usedRaw) : hasPercent && hasLimit
    ? Math.round((Math.min(100, Math.max(0, percentRaw)) / 100) * limitRaw)
    : 0;
  const limit = hasLimit ? limitRaw : null;
  const percent = hasPercent
    ? Math.min(100, Math.max(0, Math.round(percentRaw)))
    : hasLimit
      ? Math.min(100, Math.max(0, Math.round((used / limit) * 100)))
      : 0;

  return {
    used,
    limit,
    percent,
    parts: raw.parts && typeof raw.parts === 'object' ? raw.parts : undefined,
    reset_hint:
      typeof raw.reset_hint === 'string' && raw.reset_hint.trim()
        ? raw.reset_hint.trim()
        : typeof raw.resetHint === 'string' && raw.resetHint.trim()
          ? raw.resetHint.trim()
          : undefined,
  };
};

/** Percent of monthly quota used (0–100), for "Token Usage: N% used this month". */
export const quotaUsagePercent = (quota) => {
  const q = normalizeQuota(quota) || (quota && Number.isFinite(quota.percent) ? quota : null);
  if (!q) return null;
  if (Number.isFinite(q.percent)) return Math.min(100, Math.max(0, Math.round(q.percent)));
  if (Number.isFinite(q.used) && Number.isFinite(q.limit) && q.limit > 0) {
    return Math.min(100, Math.max(0, Math.round((q.used / q.limit) * 100)));
  }
  return null;
};

export const MONTHLY_QUOTA_RESET_HINT = 'Resets on the 7th of every month (UTC).';

export const quotaResetHint = (quota) =>
  (quota && typeof quota.reset_hint === 'string' && quota.reset_hint.trim())
    || MONTHLY_QUOTA_RESET_HINT;

/**
 * Ask Evans confidence badge — Lessons Learned sister.
 * Show only high / medium / low. Hide for none / missing / empty.
 * @returns {'high'|'medium'|'low'|null}
 */
export const normalizeAskEvansConfidence = (value) => {
  if (value == null || value === '') return null;
  const key = String(value).trim().toLowerCase();
  if (!key || key === 'none') return null;
  if (key === 'high' || key === 'medium' || key === 'low') return key;
  return null;
};

export const formatTokenCount = (n) => {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const count = Number(n);
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1000) return `${Math.round(count / 1000)}k`;
  return String(count);
};

export const formatUsd = (n) => {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const value = Number(n);
  if (value === 0) return '—';
  if (value < 0.01) return `$${value.toFixed(4)}`;
  return `$${value.toFixed(2)}`;
};

/** Lessons Learned sister — e.g. `1 hour ago`. */
export const formatRelativeTime = (iso, now = new Date()) => {
  if (!iso) return '—';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '—';
  const elapsed = now.getTime() - then.getTime();
  const MINUTE = 60 * 1000;
  const HOUR = 60 * MINUTE;
  const DAY = 24 * HOUR;
  if (elapsed < MINUTE) return 'just now';
  if (elapsed < HOUR) return `${Math.floor(elapsed / MINUTE)} min ago`;
  if (elapsed < DAY) {
    const hours = Math.floor(elapsed / HOUR);
    return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
  }
  const days = Math.floor(elapsed / DAY);
  return days === 1 ? '1 day ago' : `${days} days ago`;
};

/** Short display name for Anthropic Bedrock ids. */
export const formatBedrockModelLabel = (modelId) => {
  if (!modelId) return '—';
  const id = String(modelId).toLowerCase();
  if (id.includes('haiku-4-5') || id.includes('haiku-4.5')) return 'Haiku 4.5';
  if (id.includes('sonnet-4-6') || id.includes('sonnet-4.6')) return 'Sonnet 4.6';
  if (id.includes('sonnet-4-5') || id.includes('sonnet-4.5')) return 'Sonnet 4.5';
  if (id.includes('opus-4-6') || id.includes('opus-4.6')) return 'Opus 4.6';
  if (id.includes('opus-4-5') || id.includes('opus-4.5')) return 'Opus 4.5';
  if (id.includes('sonnet-4') && id.includes('20250514')) return 'Sonnet 4';
  if (id.includes('sonnet')) return 'Sonnet';
  if (id.includes('opus')) return 'Opus';
  if (id.includes('haiku')) return 'Haiku';
  const tail = String(modelId).split('/').pop() || String(modelId);
  return tail.length > 28 ? `${tail.slice(0, 26)}…` : tail;
};

export const periodLabel = (periodStart) => {
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const [year, month] = String(periodStart || '').split('-');
  const name = MONTHS[Number(month) - 1];
  return name && year ? `${name} ${year}` : '';
};

/** Extract a readable message from axios / API errors (avoid `[object Object]`). */
export const formatApiError = (err, fallback = 'Request failed') => {
  const data = err?.response?.data;
  const candidates = [
    err?.message,
    typeof data === 'string' ? data : null,
    data?.message,
    data?.error,
    data?.errorMessage,
    data?.msg,
  ];
  for (const c of candidates) {
    if (c == null) continue;
    if (typeof c === 'string' && c.trim() && c !== '[object Object]') return c.trim();
    if (typeof c === 'object') {
      try {
        return JSON.stringify(c);
      } catch {
        /* ignore */
      }
    }
  }
  if (err?.response?.status) return `${fallback} (${err.response.status})`;
  return fallback;
};

/**
 * Discover AI insight features + availability flags.
 * GET /pih/ai/insights/features?domain=spacer_log
 */
export const fetchAiInsightFeatures = async ({ domain = DOMAIN } = {}) => {
  const res = await axiosConfig.get('ai/insights/features', {
    params: { domain },
  });
  const data = unwrap(res);
  if (!data || typeof data !== 'object') {
    throw new Error('AI insights features response was empty or invalid.');
  }
  return {
    enabled: data.enabled !== false,
    provider: data.provider || null,
    bedrockModelId: data.bedrockModelId || null,
    ragConfigured: Boolean(data.ragConfigured),
    features: Array.isArray(data.features) ? data.features : [],
    quota: normalizeQuota(data.quota),
  };
};

/**
 * Generate an insight (AI or template). Supports AbortSignal for cancel.
 * POST /pih/ai/insights
 */
export const generateAiInsight = async (body, { signal } = {}) => {
  const payload = {
    domain: DOMAIN,
    ...body,
  };
  const res = await axiosConfig.post('ai/insights', payload, { signal });
  const data = unwrap(res);
  if (!data || typeof data !== 'object') {
    throw new Error('AI insight response was empty or invalid.');
  }
  return {
    ...data,
    usage: normalizeUsage(data.usage) || pickUsageFromMessage(data),
    quota: normalizeQuota(data.quota),
    confidence: normalizeAskEvansConfidence(data.confidence || data.meta?.confidence),
    feedbackTargetId:
      data.feedbackTargetId
      || data.feedbackId
      || data.queryLogId
      || data.id
      || null,
    chatSessionId: data.chatSessionId || data.chat_session_id || null,
    ragSessionId: data.ragSessionId || data.rag_session_id || data.sessionId || null,
  };
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** GET /pih/ai/insights/quota — Token Usage bar (LL sister). */
export const fetchAskEvansQuota = async () => {
  const res = await axiosConfig.get('ai/insights/quota');
  const data = unwrap(res) || res?.data || {};
  return normalizeQuota(data) || normalizeQuota(data.quota) || null;
};

/**
 * Async Ask Evans chat (Lessons Learned sister).
 * POST /pih/ai/insights/chat → 202, then poll GET /pih/ai/insights/jobs/:jobId
 */
export const sendAskChatMessage = async (body, { signal, onProgress, pollMs = 1200, timeoutMs = 300000 } = {}) => {
  const payload = {
    domain: DOMAIN,
    ...body,
    message: body.message || body.question || body.query,
  };
  const acceptRes = await axiosConfig.post('ai/insights/chat', payload, {
    signal,
    validateStatus: (s) => (s >= 200 && s < 300) || s === 202 || s === 409,
  });
  if (acceptRes.status === 409) {
    const err = new Error(
      acceptRes.data?.error
        || acceptRes.data?.message
        || 'A response is already being prepared for this chat. Please wait.'
    );
    err.response = acceptRes;
    err.data = acceptRes.data?.data || acceptRes.data || {};
    throw err;
  }
  const accepted = unwrap(acceptRes) || acceptRes?.data?.data || acceptRes?.data || {};
  const jobId = accepted.job_id || accepted.jobId;
  const sessionId = accepted.session_id || accepted.sessionId || accepted.chatSessionId;
  if (!jobId) {
    throw new Error('Ask Evans chat did not return a job_id');
  }

  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    if (signal?.aborted) {
      const abortErr = new Error('Aborted');
      abortErr.name = 'AbortError';
      throw abortErr;
    }
    await sleep(pollMs);
    const jobRes = await axiosConfig.get(`ai/insights/jobs/${encodeURIComponent(jobId)}`, { signal });
    const job = unwrap(jobRes) || jobRes?.data?.data || jobRes?.data || {};
    if (typeof onProgress === 'function' && job.progress) onProgress(job.progress);
    const status = job.status;
    if (status === 'completed') {
      return {
        markdown: job.answer || job.markdown || '',
        answer: job.answer || job.markdown || '',
        citations: job.citations || [],
        sources: job.sources || [],
        confidence: normalizeAskEvansConfidence(job.confidence || job.meta?.confidence),
        usage: normalizeUsage(job.usage),
        quota: normalizeQuota(job.quota),
        feedbackTargetId: job.feedbackTargetId || job.query_log_id || job.queryLogId || null,
        chatSessionId: job.chatSessionId || job.session_id || sessionId || null,
        ragSessionId: job.ragSessionId || null,
        jobId,
      };
    }
    if (status === 'failed') {
      throw new Error(job.error || 'Ask Evans job failed');
    }
  }
  throw new Error('Ask Evans timed out waiting for a response');
};

const unwrapSessions = (data) => {
  if (!data) return [];
  if (Array.isArray(data.sessions)) return data.sessions;
  if (Array.isArray(data)) return data;
  return [];
};

/**
 * Lessons Learned sister date for chat history rows — e.g. `30-Sep-2026`.
 */
export const formatChatSessionDate = (iso) => {
  if (iso == null || iso === '') return '';
  let d;
  if (typeof iso === 'string') {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
    d = m
      ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
      : new Date(iso);
  } else {
    d = iso instanceof Date ? iso : new Date(iso);
  }
  if (!d || Number.isNaN(d.getTime())) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleString('en-US', { month: 'short' });
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

/** @deprecated Prefer formatChatSessionDate for Ask Evans history (LL sister). */
export const formatChatRelativeTime = (iso) => formatChatSessionDate(iso);

/**
 * Map persisted thread messages into Ask Evans UI message shape.
 * Assistant `feedback.rating` restores thumbs without a separate GET.
 */
export const normalizeFeedbackRating = (feedback) => {
  if (feedback == null) return null;
  if (typeof feedback === 'string' || typeof feedback === 'number') {
    if (feedback === 'good' || feedback === 1) return 'good';
    if (feedback === 'bad' || feedback === -1) return 'bad';
    return null;
  }
  const rating = feedback.rating ?? feedback.value ?? null;
  if (rating === 'good' || rating === 1) return 'good';
  if (rating === 'bad' || rating === -1) return 'bad';
  return null;
};

export const mapChatThreadToMessages = (messages = []) => {
  const asText = (value) => {
    if (typeof value === 'string') return value;
    if (value == null) return '';
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (Array.isArray(value)) return value.map(asText).filter(Boolean).join('\n\n');
    if (typeof value === 'object') {
      if (typeof value.text === 'string') return value.text;
      if (typeof value.content === 'string') return value.content;
      if (typeof value.markdown === 'string') return value.markdown;
      if (typeof value.answer === 'string') return value.answer;
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return '';
      }
    }
    return '';
  };

  const out = [];
  let lastUserQuestion = '';
  (Array.isArray(messages) ? messages : []).forEach((m, index) => {
    if (!m || typeof m !== 'object') return;
    const role = m.role === 'assistant' || m.role === 'bot' ? 'assistant' : 'user';
    const content = asText(m.content ?? m.text ?? m.markdown ?? m.message ?? '');
    const msgId = m.id ?? m.messageId ?? m.feedbackTargetId ?? `${role}-${index}`;
    if (role === 'user') {
      lastUserQuestion = content;
      out.push({
        id: `u-${msgId}`,
        role: 'user',
        text: content,
        createdAt: m.createdAt,
      });
    } else {
      const feedbackTargetId =
        m.feedbackTargetId
        || m.feedback_target_id
        || m.feedbackId
        || m.queryLogId
        || m.id
        || null;
      const usage =
        pickUsageFromMessage(m)
        || readCachedUsage(feedbackTargetId);
      out.push({
        id: `a-${msgId}`,
        role: 'assistant',
        markdown: content,
        citations: m.citations,
        confidence: normalizeAskEvansConfidence(m.confidence || m.meta?.confidence),
        feature: m.feature,
        question: lastUserQuestion,
        usage,
        feedbackTargetId,
        feedbackRating: normalizeFeedbackRating(m.feedback || m.feedbackRating || m.rating),
        ragSessionId: m.ragSessionId || m.rag_session_id || null,
        createdAt: m.createdAt,
      });
    }
  });
  return out;
};

/** GET /pih/ai/insights/chats */
export const listAiInsightChats = async ({
  domain = DOMAIN,
  projectId,
  limit = 30,
} = {}) => {
  const params = { domain, limit };
  if (projectId != null && projectId !== '' && projectId !== 'all') {
    params.projectId = projectId;
  }
  const res = await axiosConfig.get('ai/insights/chats', { params });
  const data = unwrap(res) || res?.data || {};
  return {
    quota: normalizeQuota(data.quota),
    sessions: unwrapSessions(data).map((s) => {
      const createdAt = s.createdAt || s.created_at || s.CreatedAt || null;
      // Lessons Learned sister field is `last_active`
      const lastActive =
        s.last_active ||
        s.lastActive ||
        s.LastActive ||
        s.updatedAt ||
        s.updated_at ||
        s.UpdatedAt ||
        createdAt ||
        null;
      const messageCount =
        s.messageCount ??
        s.message_count ??
        s.messagesCount ??
        s.MessageCount ??
        (Array.isArray(s.messages) ? s.messages.length : null);
      return {
        id: s.id,
        title: (typeof s.title === 'string' && s.title.trim()) || 'Untitled chat',
        feature: s.feature || null,
        projectId: s.projectId ?? s.project_id ?? null,
        sowId: s.sowId ?? s.sow_id ?? null,
        ragSessionId: s.ragSessionId || s.rag_session_id || null,
        createdAt,
        updatedAt: lastActive,
        lastActive,
        messageCount: Number.isFinite(Number(messageCount)) ? Number(messageCount) : null,
      };
    }),
  };
};

/** GET /pih/ai/insights/chats/:id */
export const getAiInsightChat = async (id, { domain = DOMAIN, signal } = {}) => {
  const chatId = id != null ? String(id) : '';
  if (!chatId) throw new Error('Chat id is required.');

  // Unique query key avoids Express ETag → browser If-None-Match → body-less 304.
  // Never set Cache-Control / If-None-Match here — CORS preflight blocks those headers.
  const res = await axiosConfig.get(`ai/insights/chats/${encodeURIComponent(chatId)}`, {
    params: { domain, _cb: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}` },
    signal,
    timeout: 20000,
    validateStatus: (status) => status >= 200 && status < 300 && status !== 304,
  });

  const raw = res?.data;
  if (raw == null || raw === '') {
    throw new Error('Chat response was empty. Try opening the chat again.');
  }

  // Tolerate several BE envelope shapes: { message, data: { session, messages } }
  let data = unwrap(res);
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      data = raw.data && typeof raw.data === 'object' && !Array.isArray(raw.data) ? raw.data : raw;
    }
  }
  if (!data || typeof data !== 'object') {
    throw new Error('Chat thread response was empty or invalid.');
  }

  const session = data.session || data.thread || data.chat || data;
  const messages =
    (Array.isArray(data.messages) && data.messages)
    || (Array.isArray(session?.messages) && session.messages)
    || (Array.isArray(data.thread?.messages) && data.thread.messages)
    || (Array.isArray(data.items) && data.items)
    || (Array.isArray(data.history) && data.history)
    || (Array.isArray(raw?.data?.messages) && raw.data.messages)
    || (Array.isArray(raw?.messages) && raw.messages)
    || [];

  return {
    session: {
      id: session?.id || data.id || chatId,
      title: session?.title || data.title || 'Untitled chat',
      feature: session?.feature || data.feature || null,
      projectId: session?.projectId ?? data.projectId ?? null,
      sowId: session?.sowId ?? data.sowId ?? null,
      ragSessionId: session?.ragSessionId || data.ragSessionId || null,
      createdAt: session?.createdAt || data.createdAt || null,
      updatedAt: session?.updatedAt || data.updatedAt || null,
    },
    messages,
  };
};

/** GET /pih/ai/insights/chats/:id/summary — deterministic markdown for Session summary PDF */
export const getAiInsightChatSummary = async (id) => {
  const res = await axiosConfig.get(`ai/insights/chats/${encodeURIComponent(id)}/summary`);
  const data = unwrap(res) || res?.data || {};
  let summaryMarkdown =
    data.summary_markdown || data.summaryMarkdown || data.summary || '';
  // Lessons Learned sister: build markdown from sections when API returns structured summary
  if (!summaryMarkdown && data.sections && typeof data.sections === 'object') {
    const parts = [];
    const s = data.sections;
    if (s.overview) parts.push(`## Overview\n${s.overview}`);
    if (Array.isArray(s.topics) && s.topics.length) {
      parts.push(`## Topics discussed\n${s.topics.map((t) => `- ${t}`).join('\n')}`);
    }
    if (Array.isArray(s.key_findings) && s.key_findings.length) {
      parts.push(`## Key findings\n${s.key_findings.map((t) => `- ${t}`).join('\n')}`);
    }
    if (Array.isArray(s.open_questions) && s.open_questions.length) {
      parts.push(`## Open questions\n${s.open_questions.map((t) => `- ${t}`).join('\n')}`);
    }
    if (s.feedback_notes) parts.push(`## Feedback notes\n${s.feedback_notes}`);
    summaryMarkdown = parts.join('\n\n');
  }
  return {
    sessionId: data.session_id || data.sessionId || id,
    title: data.title || 'Ask Evans session',
    summaryMarkdown,
    messageCount: data.message_count ?? data.messageCount ?? 0,
    sections: data.sections || null,
  };
};

/** DELETE /pih/ai/insights/chats/:id */
export const deleteAiInsightChat = async (id) => {
  const res = await axiosConfig.delete(`ai/insights/chats/${encodeURIComponent(id)}`);
  return unwrap(res) || res?.data || { ok: true };
};

/** POST /pih/ai/insights/feedback — thumbs good/bad */
export const submitAiInsightFeedback = async ({
  feedbackTargetId,
  rating,
  domain = DOMAIN,
  feature,
  question,
  comment,
  answerPreview,
  markdown,
  projectId,
  ragSessionId,
} = {}) => {
  const payload = {
    domain,
    feedbackTargetId,
    rating: rating === 1 || rating === 'good' ? 'good' : rating === -1 || rating === 'bad' ? 'bad' : rating,
  };
  if (feature) payload.feature = feature;
  if (question) payload.question = question;
  if (comment) payload.comment = comment;
  const preview = answerPreview || markdown;
  if (preview) {
    payload.answerPreview = String(preview).slice(0, 2000);
  }
  if (projectId != null && projectId !== '') payload.projectId = projectId;
  if (ragSessionId) payload.ragSessionId = ragSessionId;
  const res = await axiosConfig.post('ai/insights/feedback', payload);
  return unwrap(res) || res?.data || { ok: true };
};

/** POST /pih/ai/insights/pdf — markdown → PDF blob */
export const downloadAiInsightPdf = async ({
  markdown,
  title = 'Ask Evans Report',
  domain = DOMAIN,
  feature,
  question,
} = {}) => {
  const payload = { domain, markdown, title };
  if (feature) payload.feature = feature;
  if (question) payload.question = question;
  const res = await axiosConfig.post('ai/insights/pdf', payload, {
    responseType: 'blob',
  });
  const blob = res?.data instanceof Blob
    ? res.data
    : new Blob([res?.data], { type: 'application/pdf' });
  if (!blob || blob.size === 0) {
    throw new Error('PDF response was empty.');
  }
  if (blob.type && blob.type.includes('json')) {
    const text = await blob.text();
    let msg = 'Failed to download PDF.';
    try {
      const parsed = JSON.parse(text);
      msg = parsed.message || parsed.error || msg;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  return blob;
};

export const saveBlobAsFile = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const AI_INSIGHTS_DOMAIN = DOMAIN;

export const SPACER_LOG_AI_FEATURES = {
  PERIOD_BRIEF: 'period_brief',
  SHIFT_BRIEF: 'shift_brief',
  BOTTLENECK_COACH: 'bottleneck_coach',
  COMMENT_THEMES: 'comment_themes',
  COMPLIANCE_ASSISTANT: 'compliance_assistant',
  ANOMALY_EXPLAIN: 'anomaly_explain',
  NL_QA: 'nl_qa',
  RAG_QA: 'rag_qa',
};

export const DAILY_AI_FEATURE_IDS = [
  SPACER_LOG_AI_FEATURES.SHIFT_BRIEF,
  SPACER_LOG_AI_FEATURES.BOTTLENECK_COACH,
  SPACER_LOG_AI_FEATURES.COMMENT_THEMES,
  SPACER_LOG_AI_FEATURES.COMPLIANCE_ASSISTANT,
  SPACER_LOG_AI_FEATURES.ANOMALY_EXPLAIN,
];

export const routeAskQuestion = (question, ragConfigured) => {
  const q = String(question || '').toLowerCase();
  const looksLikeDocs =
    /sop|procedure|manual|policy|report wording|close-?out checklist|golfinho|document|pdf/i.test(
      q
    );
  if (looksLikeDocs && ragConfigured) return SPACER_LOG_AI_FEATURES.RAG_QA;
  return SPACER_LOG_AI_FEATURES.NL_QA;
};

export const isAiInsightsUiEnabled = () => {
  const flag = process.env.REACT_APP_AI_INSIGHTS;
  if (flag == null || flag === '') return true;
  return flag === 'true' || flag === '1';
};

/** —— Admin: AI Configuration + Ask Evans audit —— */

export const fetchAiAdminSettings = async () => {
  const res = await axiosConfig.get('ai/admin/settings');
  const data = unwrap(res) || res?.data || {};
  return {
    settings: data.settings || {},
    runtime: data.runtime || {},
  };
};

export const saveAiAdminSettings = async (payload) => {
  const res = await axiosConfig.put('ai/admin/settings', payload);
  const data = unwrap(res) || res?.data || {};
  return {
    settings: data.settings || {},
    runtime: data.runtime || {},
  };
};

export const fetchBedrockModels = async (region) => {
  const res = await axiosConfig.get('ai/admin/bedrock/models', {
    params: region ? { region } : undefined,
  });
  const data = unwrap(res) || res?.data || {};
  return {
    region: data.region || region || '',
    models: Array.isArray(data.models) ? data.models : [],
    reason: data.reason || '',
  };
};

export const testBedrockModel = async ({ modelId, region }) => {
  const res = await axiosConfig.post('ai/admin/bedrock/test', { modelId, region });
  return unwrap(res) || res?.data || { ok: false, message: 'No response' };
};

/** GET /pih/ai/admin/usage — Allowance tab (Lessons Learned sister). */
export const fetchAiAdminUsage = async ({ period } = {}) => {
  const res = await axiosConfig.get('ai/admin/usage', {
    params: period ? { period } : undefined,
  });
  const data = unwrap(res) || res?.data || {};
  const users = (Array.isArray(data.users) ? data.users : []).map((u) => {
    const workflow = u.workflow || {};
    const assistant = u.assistant || {};
    const bucket = (b) => ({
      used: Number(b.used || 0) || 0,
      allowance: Number(b.allowance || 0) || 0,
      percentage: Number(b.percentage || 0) || 0,
      status: b.status || 'ok',
    });
    return {
      userSub: u.userSub || u.user_sub || u.email || '',
      name: u.name || u.userName || u.user_name || u.email || 'User',
      email: u.email || u.userEmail || '',
      tier: u.tier || 'standard',
      tierLabel: u.tierLabel || u.tier_label || 'Standard',
      workflow: bucket(workflow),
      assistant: bucket(assistant),
      cost: u.cost == null ? null : Number(u.cost),
      lastUsedAt: u.lastUsedAt || u.last_used_at || null,
    };
  });
  return {
    periodStart: data.periodStart || data.period_start || period || '',
    resetDate: data.resetDate || data.reset_date || '',
    periods: Array.isArray(data.periods) ? data.periods : [],
    users,
    totals: {
      workflowUsed: Number(data.totals?.workflowUsed ?? data.totals?.workflow_used ?? 0) || 0,
      assistantUsed: Number(data.totals?.assistantUsed ?? data.totals?.assistant_used ?? 0) || 0,
      cost:
        data.totals?.cost == null || data.totals?.cost === ''
          ? null
          : Number(data.totals.cost),
    },
  };
};

export const fetchAskEvansAudit = async ({
  period,
  userEmail,
  userSub,
  status,
  limit = 25,
  offset = 0,
} = {}) => {
  const params = { limit, offset };
  if (period) params.period = period;
  if (status && status !== 'all') params.status = status;
  if (userEmail) params.userEmail = userEmail;
  if (userSub) params.userSub = userSub;

  const res = await axiosConfig.get('ai/admin/usage/ask-evans', { params });
  const data = unwrap(res) || res?.data || {};
  const rawItems = Array.isArray(data.items)
    ? data.items
    : Array.isArray(data.rows)
      ? data.rows
      : Array.isArray(data.data)
        ? data.data
        : [];

  const items = rawItems.map((row, index) => {
    const inputTokens = Number(row.inputTokens ?? row.input_tokens ?? 0) || 0;
    const outputTokens = Number(row.outputTokens ?? row.output_tokens ?? 0) || 0;
    const totalTokens =
      Number(row.totalTokens ?? row.total_tokens ?? inputTokens + outputTokens) || 0;
    return {
      jobId: String(row.jobId ?? row.job_id ?? row.id ?? `row-${index}`),
      createdAt: row.createdAt || row.created_at || null,
      userEmail: row.userEmail || row.user_email || null,
      userName: row.userName || row.user_name || null,
      userSub: row.userSub || row.user_sub || null,
      status: row.status || 'completed',
      question: row.question || row.prompt || '',
      answer: row.answer || row.response || row.answerPreview || '',
      error: row.error || row.errorMessage || null,
      modelId: row.modelId || row.model_id || row.model || null,
      inputTokens,
      outputTokens,
      totalTokens,
      cost: Number(row.cost ?? row.estimatedCostUsd ?? row.estimated_cost_usd ?? NaN),
      feature: row.feature || null,
      toolsUsed: Array.isArray(row.toolsUsed)
        ? row.toolsUsed
        : Array.isArray(row.tools_used)
          ? row.tools_used
          : [],
      toolsCalled: Number(row.toolsCalled ?? row.tools_called ?? 0) || 0,
      bedrockCallCount: Number(
        row.bedrockCallCount ?? row.bedrock_call_count ?? row.calls?.length ?? 0
      ) || 0,
      priceRate: row.priceRate || row.price_rate || null,
      calls: Array.isArray(row.calls)
        ? row.calls.map((c, i) => ({
            id: c.id ?? i,
            modelId: c.modelId || c.model_id || null,
            inputTokens: Number(c.inputTokens ?? c.input_tokens ?? 0) || 0,
            outputTokens: Number(c.outputTokens ?? c.output_tokens ?? 0) || 0,
            totalTokens:
              Number(
                c.totalTokens ??
                  c.total_tokens ??
                  (Number(c.inputTokens ?? c.input_tokens ?? 0) || 0) +
                    (Number(c.outputTokens ?? c.output_tokens ?? 0) || 0)
              ) || 0,
            cost: Number(c.cost ?? c.estimatedCostUsd ?? NaN),
            succeeded: c.succeeded !== false && c.status !== 'failed',
            createdAt: c.createdAt || c.created_at || null,
          }))
        : [],
    };
  });

  const users = (Array.isArray(data.users) ? data.users : []).map((u) => {
    const email = u.userEmail || u.user_email || u.email || '';
    const name = u.userName || u.user_name || u.name || '';
    const sub = u.userSub || u.user_sub || u.sub || '';
    return {
      userEmail: email,
      userName: name,
      userSub: sub,
      label: name && email ? `${name} (${email})` : email || name || sub || 'User',
    };
  });

  return {
    periodStart: data.periodStart || data.period_start || period || '',
    resetDate: data.resetDate || data.reset_date || '',
    total: Number(data.total ?? data.count ?? items.length) || 0,
    limit: Number(data.limit || limit),
    offset: Number(data.offset || offset),
    users,
    items,
  };
};
